import { useRef } from 'react';
import styled from 'styled-components';
import { Skeleton, Tooltip, Typography } from '@mui/material';
import Humanize from 'humanize-plus';
import { ReactComponent as InfoIcon } from '../../../assets/info-icon.svg';
import { Flex, FlexColumn } from '../../../components/Flex';
import useHover from '../../../hooks/useHover';

const Wrapper = styled(FlexColumn)`
  min-width: 225px;
  width: 225px;
  gap: 6px;
  align-items: center;
`;

const Value = styled(Typography)`
  font-size: 37px;
  font-weight: 300;
  :hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const Details = styled(Flex)`
  align-items: center;
  gap: 12px;
`;

const Divider = styled.div`
  height: 18px;
  width: 1px;
  background: ${({ theme }) => theme.palette.common.gray1};
`;

const Detail = styled(Typography)<{ color: string }>`
  font-size: 24px;
  color: ${({ theme, color }) => theme.palette[color].main};
  :hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const Title = styled(Typography)`
  font-size: 18px;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const TitleWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
`;

type Props = {
  title: string;
  data: {
    title: string;
    passed: number;
    warnings: number;
    failures: number;
    errors: number;
  };
  tooltip: string;
  onValueClick: () => void;
  onLabelClick: (statuses: string[]) => void;
  loading?: boolean;
};

const SummaryBox = ({
  title,
  data,
  tooltip,
  onValueClick,
  onLabelClick,
  loading,
}: Props) => {
  const wrapperRef = useRef<any>();
  const isHover = useHover(wrapperRef);

  return (
    <Wrapper ref={wrapperRef} aria-label={`${title} summary`}>
      {loading ? (
        <FlexColumn sx={{ gap: 1, alignItems: 'center' }}>
          <FlexColumn>
            <Skeleton variant="text" width={50} height={32} />
            <Skeleton variant="text" width={50} height={57} />
          </FlexColumn>
          <Flex sx={{ gap: 1 }}>
            <Skeleton variant="text" width={30} height={34} />
            <Skeleton variant="text" width={30} height={34} />
            <Skeleton variant="text" width={30} height={34} />
          </Flex>
        </FlexColumn>
      ) : (
        <>
          <TitleWrapper>
            <Flex sx={{ gap: 0.7, alignItems: 'center', ml: '19px' }}>
              <Title>{title}</Title>
              <Tooltip
                title={tooltip}
                sx={{ visibility: isHover ? 'visible' : 'hidden' }}
              >
                <Flex>
                  <InfoIcon />
                </Flex>
              </Tooltip>
            </Flex>
          </TitleWrapper>
          <Value onClick={onValueClick} aria-label={`${title} total value`}>
            {Humanize.formatNumber(data.passed + data.warnings + data.failures)}
          </Value>
          <Details divider={<Divider />}>
            <Detail
              color="success"
              onClick={() => onLabelClick(['passed'])}
              aria-label={`${title} passed count`}
            >
              {Humanize.formatNumber(data.passed)}
            </Detail>
            <Detail
              color="warning"
              onClick={() => onLabelClick(['warning'])}
              aria-label={`${title} warnings count`}
            >
              {Humanize.formatNumber(data.warnings)}
            </Detail>
            <Detail
              color="error"
              onClick={() => onLabelClick(['failed', 'error'])}
              aria-label={`${title} failures count`}
            >
              {Humanize.formatNumber(data.failures + data.errors)}
            </Detail>
          </Details>
        </>
      )}
    </Wrapper>
  );
};

export default SummaryBox;
